import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles, Clock } from "lucide-react";
import { CONTACT, scrollToId } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import laptopLogo from "@/assets/laptop-logo.png";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.13 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const scrollToContact = () => scrollToId("#contact");
  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden pt-32 pb-16">
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -top-40 -right-32 h-[42rem] w-[42rem] rounded-full bg-primary/20 blur-[140px] animate-pulse-glow"
      />
      <div className="pointer-events-none absolute -bottom-52 -left-40 h-[36rem] w-[36rem] rounded-full bg-accent/10 blur-[150px]" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-4 py-1.5 mb-8"
            data-testid="hero-badge"
          >
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-mono-x uppercase tracking-[0.2em] text-muted-foreground">
              {t.hero.badge}
            </span>
          </motion.div>

          <h1 className="font-heading font-extrabold tracking-tighter leading-[0.92] text-5xl sm:text-6xl lg:text-7xl">
            {lines.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className={`block ${i === 2 ? "text-primary text-glow" : "text-foreground"}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-8 max-w-xl text-base lg:text-lg text-muted-foreground leading-relaxed"
          >
            {t.hero.paragraph}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={scrollToContact}
              data-testid="hero-get-started-button"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground hover:bg-accent transition-colors duration-300"
            >
              {t.hero.getStarted}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <div className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3" data-testid="hero-hours-badge">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">{t.hero.hours}</span>
            </div>
          </motion.div>

          <motion.a
            href={CONTACT.phoneHref}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            data-testid="hero-phone-link"
            className="mt-8 inline-block font-mono-x text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {t.hero.call} {CONTACT.phone}
          </motion.a>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-2xl overflow-hidden border border-border shadow-2xl"
            style={{ boxShadow: "0 0 80px -20px hsl(174 82% 44% / 0.35)" }}
          >
            <motion.img
              src={laptopLogo}
              alt="LocalNova logo on a laptop screen"
              style={{ y: yImg, scale: scaleImg }}
              className="w-full h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/10 to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="absolute -bottom-6 -left-6 rounded-xl border border-border bg-card/90 backdrop-blur-md px-5 py-4"
            data-testid="hero-stat-card"
          >
            <p className="font-heading text-3xl font-bold text-primary">$299</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t.hero.statNote}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
